import { motion } from 'framer-motion';
import { useApp } from '../../context/AppContext';
import { REASONS } from '../../data/content';
import { Reveal } from '../ui/Reveal';
import { Icons } from '../ui/Icons';

export function WhyRosa() {
  const { openLeadForm } = useApp();

  return (
    <section id="why" className="relative overflow-hidden bg-blush py-20 sm:py-28">
      <div className="pointer-events-none absolute -right-24 top-24 h-80 w-80 rounded-full bg-terracotta/8 blur-3xl" />

      <div className="container-rosa relative grid gap-12 lg:grid-cols-2 lg:gap-16">
        {/* Left — editorial narrative + documentary image */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <Reveal>
            <p className="eyebrow">Why families trust Rosa</p>
            <h2 className="mt-4 font-display text-4xl font-bold leading-[1.06] tracking-[-0.01em] text-plum sm:text-5xl">
              The hard part is already done.
            </h2>
            <p className="mt-6 max-w-md font-body text-lg leading-relaxed text-plum/70">
              When a parent comes home from the hospital, no family should have to make five cold calls at 2am and
              hope a stranger knows what they are doing. Rosa has already done the vetting. Every specialist is
              CAPS-certified and background-checked, so the frightened, exhausted part of this is off your plate before
              you ever pick up the phone.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative mt-10 max-w-md">
              <div className="overflow-hidden rounded-[1.5rem] border-4 border-white shadow-lift">
                <img
                  src="/images/contractor-explaining.webp"
                  alt="A CAPS-certified specialist explaining a grab bar to a homeowner at eye level"
                  className="aspect-[5/4] w-full object-cover"
                  width={640}
                  height={512}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-plum/20 via-transparent to-transparent" />
              </div>

              {/* Floating vetted chip */}
              <motion.div
                initial={{ opacity: 0, y: 18, scale: 0.92 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="absolute -bottom-4 -right-3 flex items-center gap-3 rounded-2xl border border-stone bg-white/95 p-3 pr-4 shadow-lift backdrop-blur sm:-right-6"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-sage/15 text-sage-deep">
                  <Icons.badge width={24} height={24} />
                </span>
                <div>
                  <p className="font-display text-sm font-bold leading-none text-plum">Vetted</p>
                  <p className="mt-1 font-body text-xs text-plum/55">&amp; background-checked</p>
                </div>
              </motion.div>
            </div>
          </Reveal>
        </div>

        {/* Right — reasons as premium vertical stack */}
        <div>
          <ul className="flex flex-col">
            {REASONS.map((reason, i) => {
              const I = Icons[reason.icon as keyof typeof Icons];
              return (
                <Reveal
                  as="li"
                  key={reason.id}
                  delay={i * 0.1}
                  className={`flex gap-5 py-7 ${i > 0 ? 'border-t border-stone' : ''}`}
                >
                  <span className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-terracotta/12 text-terracotta">
                    <I width={26} height={26} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-lg font-semibold leading-snug text-plum sm:text-xl">
                        {reason.title}
                      </h3>
                      {reason.stat && (
                        <span className="mt-0.5 shrink-0 rounded-full bg-sage/12 px-3 py-1 font-body text-xs font-semibold text-sage-deep">
                          {reason.stat}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 font-body leading-relaxed text-plum/70">{reason.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </ul>

          <Reveal delay={0.15}>
            <div className="mt-8 border-t border-stone pt-8">
              <button onClick={() => openLeadForm()} className="btn-primary cursor-pointer">
                Get a free safety assessment
                <Icons.arrowRight width={18} height={18} />
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
